import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/Card';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { apiClient } from '../../lib/api/client';
import { toast } from 'react-hot-toast';
import { User, Mail, Phone, Shield, Calendar, Lock, Key, Edit3, Save, X, Wifi, WifiOff, Copy, CheckCircle } from 'lucide-react';

const UserProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', exotel_virtual_number: '', exotel_agent_number: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [originalForm, setOriginalForm] = useState({ first_name: '', last_name: '', phone: '', exotel_virtual_number: '', exotel_agent_number: '' });
  
  // VOIP Settings
  const [voipSettings, setVoipSettings] = useState<{
    voip_enabled: boolean;
    user_voip_enabled: boolean;
    voip_username: string | null;
    sip_uri: string | null;
    voip_domain: string;
    message: string;
  } | null>(null);
  const [loadingVoip, setLoadingVoip] = useState(false);
  const [savingVoip, setSavingVoip] = useState(false);
  const [copiedSipUri, setCopiedSipUri] = useState(false);
  const [voipCredentials, setVoipCredentials] = useState({
    username: '',
    password: ''
  });
  const [isEditingVoip, setIsEditingVoip] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ 
        first_name: user.first_name || '', 
        last_name: user.last_name || '', 
        phone: user.phone || '',
        exotel_virtual_number: (user as any).exotel_virtual_number || '',
        exotel_agent_number: (user as any).exotel_agent_number || ''
      });
      setOriginalForm({ 
        first_name: user.first_name || '', 
        last_name: user.last_name || '', 
        phone: user.phone || '',
        exotel_virtual_number: (user as any).exotel_virtual_number || '',
        exotel_agent_number: (user as any).exotel_agent_number || ''
      });
      
      // Fetch VOIP settings
      fetchVoipSettings();
    }
  }, [user]);

  const fetchVoipSettings = async () => {
    try {
      setLoadingVoip(true);
      const settings = await apiClient.getVoipSettings();
      setVoipSettings(settings);
    } catch (error: any) {
      console.error('Failed to fetch VOIP settings:', error);
      // Silently fail - VOIP may not be available
    } finally {
      setLoadingVoip(false);
    }
  };

  const handleEnableVoip = async () => {
    if (!voipSettings) return;
    
    // Validate credentials
    if (!voipCredentials.username || !voipCredentials.password) {
      toast.error('Please enter your Exotel VOIP username and password');
      return;
    }
    
    try {
      setSavingVoip(true);
      const result = await apiClient.updateVoipSettings({
        voip_enabled: true,
        voip_username: voipCredentials.username,
        voip_password: voipCredentials.password
      });
      
      setVoipSettings({
        ...voipSettings,
        user_voip_enabled: result.voip_enabled,
        voip_username: result.voip_username,
        sip_uri: result.sip_uri,
        message: result.message
      });
      
      setIsEditingVoip(false);
      setVoipCredentials({ username: '', password: '' });
      toast.success(result.message || 'VOIP enabled successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to enable VOIP');
    } finally {
      setSavingVoip(false);
    }
  };

  const handleDisableVoip = async () => {
    if (!voipSettings) return;
    
    try {
      setSavingVoip(true);
      const result = await apiClient.updateVoipSettings({
        voip_enabled: false
      });
      
      setVoipSettings({
        ...voipSettings,
        user_voip_enabled: result.voip_enabled,
        voip_username: null,
        sip_uri: null,
        message: result.message
      });
      
      toast.success(result.message || 'VOIP disabled successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to disable VOIP');
    } finally {
      setSavingVoip(false);
    }
  };

  const handleCopySipUri = () => {
    if (voipSettings?.sip_uri) {
      navigator.clipboard.writeText(voipSettings.sip_uri);
      setCopiedSipUri(true);
      toast.success('SIP URI copied to clipboard');
      setTimeout(() => setCopiedSipUri(false), 2000);
    }
  };

  const handleCancelEdit = () => {
    setForm(originalForm);
    setIsEditingProfile(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      'admin': 'Administrator',
      'recruiter': 'Recruiter',
      'team_lead': 'Team Lead',
      'ops_head': 'Operations Head',
      'ceo': 'CEO',
      'manager': 'Manager',
      'viewer': 'Viewer',
      'mis_coordinator': 'MIS Coordinator',
      'hr_coordinator': 'HR Coordinator'
    };
    return roleMap[role] || role;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-200 via-background-300 to-background-400 p-6">
      <ResponsiveContainer className="max-w-7xl mx-auto space-y-8">
        {/* Animated Background Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-500/20 via-secondary-500/10 to-accent-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-accent-500/20 via-primary-500/10 to-secondary-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-secondary-500/10 via-accent-500/20 to-primary-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
          {/* Left Column - Profile Overview */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <Card className="text-center bg-gradient-to-br from-glass-surface/80 via-glass-surface/90 to-glass-surface/95 border border-primary-500/30 shadow-glass-lg hover:shadow-glow-primary transition-all duration-500 hover:scale-105">
              <CardHeader>
                <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 rounded-full flex items-center justify-center shadow-glow-primary animate-pulse">
                  <User className="w-16 h-16 text-white drop-shadow-lg" />
                </div>
                <CardTitle className="text-2xl bg-gradient-to-r from-text-enterprise-primary to-text-enterprise-accent bg-clip-text text-transparent">
                  {user.first_name} {user.last_name}
                </CardTitle>
                <CardDescription className="text-text-enterprise-secondary text-lg">
                  {getRoleDisplayName(user.role)}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center gap-3 text-sm text-text-enterprise-secondary bg-glass-surface/40 p-3 rounded-xl border border-glass-border/30">
                  <Mail className="w-5 h-5 text-primary-500" />
                  <span>{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center justify-center gap-3 text-sm text-text-enterprise-secondary bg-glass-surface/40 p-3 rounded-xl border border-glass-border/30">
                    <Phone className="w-5 h-5 text-secondary-500" />
                    <span>{user.phone}</span>
                  </div>
                )}
                {(user as any).exotel_virtual_number && (
                  <div className="flex items-center justify-center gap-3 text-sm text-text-enterprise-secondary bg-glass-surface/40 p-3 rounded-xl border border-glass-border/30">
                    <Phone className="w-5 h-5 text-accent-500" />
                    <span>Virtual: {(user as any).exotel_virtual_number}</span>
                  </div>
                )}
                {(user as any).exotel_agent_number && (
                  <div className="flex items-center justify-center gap-3 text-sm text-text-enterprise-secondary bg-glass-surface/40 p-3 rounded-xl border border-glass-border/30">
                    <Phone className="w-5 h-5 text-accent-600" />
                    <span>Agent: {(user as any).exotel_agent_number}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Status */}
            <Card className="bg-gradient-to-br from-glass-surface/70 via-glass-surface/80 to-glass-surface/90 border border-success-500/30 shadow-glass-lg hover:shadow-glow-success transition-all duration-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-lg">
                  <Shield className="w-6 h-6 text-success-500" />
                  <span className="bg-gradient-to-r from-success-500 to-success-600 bg-clip-text text-transparent">Account Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                  <span className="text-sm text-text-enterprise-secondary">Status</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium shadow-sm ${
                    user.is_active ? 'bg-gradient-to-r from-success-100 to-success-200 text-success-700 border border-success-300' : 'bg-gradient-to-r from-error-100 to-error-200 text-error-700 border border-error-300'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                  <span className="text-sm text-text-enterprise-secondary">Role</span>
                  <span className="text-sm font-medium text-text-enterprise-primary">
                    {getRoleDisplayName(user.role)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                  <span className="text-sm text-text-enterprise-secondary">Organization</span>
                  <span className="text-sm font-medium text-text-enterprise-primary">
                    {user.org_id}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Activity Info */}
            <Card className="bg-gradient-to-br from-glass-surface/70 via-glass-surface/80 to-glass-surface/90 border border-info-500/30 shadow-glass-lg hover:shadow-glow-info transition-all duration-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-lg">
                  <Calendar className="w-6 h-6 text-info-500" />
                  <span className="bg-gradient-to-r from-info-500 to-info-600 bg-clip-text text-transparent">Activity</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                  <span className="text-sm text-text-enterprise-secondary">Member Since</span>
                  <span className="text-sm font-medium text-text-enterprise-primary">
                    {formatDate(user.created_at)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                  <span className="text-sm text-text-enterprise-secondary">Last Login</span>
                  <span className="text-sm font-medium text-text-enterprise-primary">
                    {formatDate((user as any).last_login || '')}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                  <span className="text-sm text-text-enterprise-secondary">Last Updated</span>
                  <span className="text-sm font-medium text-text-enterprise-primary">
                    {formatDate(user.updated_at)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Edit Forms */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <Card className="bg-gradient-to-br from-glass-surface/80 via-glass-surface/90 to-glass-surface/95 border border-primary-500/40 shadow-glass-lg hover:shadow-glow-primary transition-all duration-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3 text-xl">
                      <User className="w-6 h-6 text-primary-500" />
                      <span className="bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">Personal Information</span>
                    </CardTitle>
                    <CardDescription className="text-text-enterprise-secondary mt-2">
                      Update your personal details and contact information
                    </CardDescription>
                  </div>
                  {!isEditingProfile ? (
                    <Button
                      variant="premium"
                      size="sm"
                      onClick={() => setIsEditingProfile(true)}
                      className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </Button>
                      <Button
                        variant="luxury"
                        size="sm"
                        onClick={async() => {
                          try {
                            setSavingPhone(true);
                            await apiClient.updateUserProfile({
                              first_name: form.first_name,
                              last_name: form.last_name,
                              phone: form.phone,
                              exotel_virtual_number: form.exotel_virtual_number,
                              exotel_agent_number: form.exotel_agent_number
                            });
                            toast.success('Profile updated successfully');
                            setOriginalForm(form);
                            setIsEditingProfile(false);
                          } catch(e: any) {
                            toast.error(e?.response?.data?.detail || 'Failed to update profile');
                          } finally {
                            setSavingPhone(false);
                          }
                        }}
                        disabled={savingPhone}
                        className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                      >
                        <Save className="w-4 h-4" />
                        {savingPhone ? 'Saving...' : 'Save All'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      First Name
                    </label>
                    <input
                      value={form.first_name}
                      onChange={(e) => setForm({...form, first_name: e.target.value})}
                      disabled={!isEditingProfile}
                      className="glass-input w-full disabled:opacity-50 disabled:cursor-not-allowed border border-primary-500/30 focus:border-primary-500/60 transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Last Name
                    </label>
                    <input
                      value={form.last_name}
                      onChange={(e) => setForm({...form, last_name: e.target.value})}
                      disabled={!isEditingProfile}
                      className="glass-input w-full disabled:opacity-50 disabled:cursor-not-allowed border border-secondary-500/30 focus:border-secondary-500/60 transition-colors duration-300"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Email Address
                    </label>
                    <input
                      value={user.email}
                      disabled
                      className="glass-input w-full disabled:opacity-50 disabled:cursor-not-allowed bg-glass-surface/30 border border-glass-border/50"
                    />
                    <p className="text-xs text-text-enterprise-secondary mt-2 p-2 bg-glass-surface/40 rounded-lg border border-glass-border/30">
                      Email cannot be changed. Contact your administrator if needed.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Phone Number
                    </label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      disabled={!isEditingProfile}
                      className="glass-input w-full disabled:opacity-50 disabled:cursor-not-allowed border border-accent-500/30 focus:border-accent-500/60 transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Exotel Virtual Number
                    </label>
                    <input
                      value={form.exotel_virtual_number}
                      onChange={(e) => setForm({...form, exotel_virtual_number: e.target.value})}
                      disabled={!isEditingProfile}
                      className="glass-input w-full disabled:opacity-50 disabled:cursor-not-allowed border border-info-500/30 focus:border-info-500/60 transition-colors duration-300"
                      placeholder="Virtual number (CallerId)"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Exotel Agent Number
                    </label>
                    <input
                      value={form.exotel_agent_number}
                      onChange={(e) => setForm({...form, exotel_agent_number: e.target.value})}
                      disabled={!isEditingProfile}
                      className="glass-input w-full disabled:opacity-50 disabled:cursor-not-allowed border border-warning-500/30 focus:border-warning-500/60 transition-colors duration-300"
                      placeholder="Agent/recruiter phone number"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card className="bg-gradient-to-br from-glass-surface/80 via-glass-surface/90 to-glass-surface/95 border border-error-500/40 shadow-glass-lg hover:shadow-glow-error transition-all duration-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-xl">
                  <Lock className="w-6 h-6 text-error-500" />
                  <span className="bg-gradient-to-r from-error-500 to-error-600 bg-clip-text text-transparent">Security Settings</span>
                </CardTitle>
                <CardDescription className="text-text-enterprise-secondary mt-2">
                  Change your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                      className="glass-input w-full border border-error-500/30 focus:border-error-500/60 transition-colors duration-300"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      className="glass-input w-full border border-error-500/30 focus:border-error-500/60 transition-colors duration-300"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-enterprise-primary mb-3">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      className="glass-input w-full border border-error-500/30 focus:border-error-500/60 transition-colors duration-300"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <div className="mt-8 flex justify-end">
                  <Button
                    onClick={async() => {
                      if (passwordForm.new_password !== passwordForm.confirm_password) {
                        toast.error('Passwords do not match');
                        return;
                      }
                      if (passwordForm.new_password.length < 8) {
                        toast.error('Password must be at least 8 characters long');
                        return;
                      }
                      try {
                        setSavingPassword(true);
                        await apiClient.changePassword({
                          current_password: passwordForm.current_password,
                          new_password: passwordForm.new_password
                        });
                        toast.success('Password changed successfully');
                        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                      } catch(e: any) {
                        toast.error(e?.response?.data?.detail || 'Failed to change password');
                      } finally {
                        setSavingPassword(false);
                      }
                    }}
                    disabled={savingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                    variant="destructive"
                    className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                  >
                    <Key className="w-4 h-4" />
                    {savingPassword ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* VOIP Settings */}
            {voipSettings && voipSettings.voip_enabled && (
              <Card className="bg-gradient-to-br from-glass-surface/80 via-glass-surface/90 to-glass-surface/95 border border-cyan-500/40 shadow-glass-lg hover:shadow-glow-primary transition-all duration-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-xl">
                    <Wifi className="w-6 h-6 text-cyan-500" />
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">VOIP Settings</span>
                  </CardTitle>
                  <CardDescription className="text-text-enterprise-secondary mt-2">
                    Configure browser-based calling for candidate outreach
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {loadingVoip ? (
                    <div className="text-center py-8 text-text-enterprise-secondary">
                      Loading VOIP settings...
                    </div>
                  ) : (
                    <>
                      {/* VOIP Status */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-glass-surface/40 rounded-xl border border-glass-border/30">
                          <div className="flex items-center gap-3">
                            {voipSettings.user_voip_enabled ? (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-success-500/20 to-success-600/20 flex items-center justify-center">
                                <Wifi className="w-6 h-6 text-success-500" />
                              </div>
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-500/20 to-gray-600/20 flex items-center justify-center">
                                <WifiOff className="w-6 h-6 text-gray-500" />
                              </div>
                            )}
                            <div>
                              <h4 className="text-base font-semibold text-text-enterprise-primary">
                                {voipSettings.user_voip_enabled ? 'VOIP Enabled' : 'VOIP Disabled'}
                              </h4>
                              <p className="text-sm text-text-enterprise-secondary">
                                {voipSettings.user_voip_enabled 
                                  ? 'Web calling is active for your account' 
                                  : 'Enable to make calls from your browser'}
                              </p>
                            </div>
                          </div>
                          {voipSettings.user_voip_enabled ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleDisableVoip}
                              disabled={savingVoip}
                              className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                            >
                              {savingVoip ? 'Disabling...' : 'Disable'}
                            </Button>
                          ) : (
                            <Button
                              variant="luxury"
                              size="sm"
                              onClick={() => setIsEditingVoip(true)}
                              disabled={savingVoip}
                              className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                            >
                              Enable
                            </Button>
                          )}
                        </div>

                        {/* Credential Input Form (shown when enabling) */}
                        {!voipSettings.user_voip_enabled && isEditingVoip && (
                          <div className="space-y-4 p-4 bg-gradient-to-br from-cyan-500/5 to-blue-600/5 rounded-xl border border-cyan-500/30">
                            <p className="text-sm text-text-enterprise-secondary">
                              Enter your Exotel VOIP credentials from the Exotel dashboard
                            </p>
                            
                            <div>
                              <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                                VOIP Username
                              </label>
                              <input
                                type="text"
                                value={voipCredentials.username}
                                onChange={(e) => setVoipCredentials({ ...voipCredentials, username: e.target.value })}
                                placeholder="e.g., admino9d2b3d5b"
                                className="w-full px-3 py-2 bg-glass-surface/60 rounded-lg border border-glass-border/30 text-sm text-text-enterprise-primary focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                                VOIP Password
                              </label>
                              <input
                                type="password"
                                value={voipCredentials.password}
                                onChange={(e) => setVoipCredentials({ ...voipCredentials, password: e.target.value })}
                                placeholder="Enter your VOIP password"
                                className="w-full px-3 py-2 bg-glass-surface/60 rounded-lg border border-glass-border/30 text-sm text-text-enterprise-primary focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                              />
                            </div>

                            <div className="flex gap-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setIsEditingVoip(false);
                                  setVoipCredentials({ username: '', password: '' });
                                }}
                                disabled={savingVoip}
                                className="flex items-center gap-2"
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </Button>
                              <Button
                                variant="luxury"
                                size="sm"
                                onClick={handleEnableVoip}
                                disabled={savingVoip || !voipCredentials.username || !voipCredentials.password}
                                className="flex items-center gap-2"
                              >
                                <Save className="w-4 h-4" />
                                {savingVoip ? 'Enabling...' : 'Enable VOIP'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* VOIP Details (shown when enabled) */}
                      {voipSettings.user_voip_enabled && voipSettings.sip_uri && (
                        <div className="space-y-4 p-4 bg-gradient-to-br from-cyan-500/5 to-blue-600/5 rounded-xl border border-cyan-500/30">
                          <div className="flex items-center gap-2 text-success-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-semibold">VOIP Active</span>
                          </div>
                          
                          {/* VOIP Username */}
                          {voipSettings.voip_username && (
                            <div>
                              <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                                VOIP Username
                              </label>
                              <div className="px-3 py-2 bg-glass-surface/60 rounded-lg border border-glass-border/30 text-sm text-text-enterprise-primary font-mono">
                                {voipSettings.voip_username}
                              </div>
                              <p className="text-xs text-text-enterprise-tertiary mt-1">
                                Your Exotel VOIP username for SIP authentication
                              </p>
                            </div>
                          )}

                          {/* SIP URI */}
                          <div>
                            <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                              SIP URI
                            </label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 px-3 py-2 bg-glass-surface/60 rounded-lg border border-glass-border/30 text-sm text-text-enterprise-primary font-mono break-all">
                                {voipSettings.sip_uri}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCopySipUri}
                                className="flex items-center gap-2 hover:scale-105 transition-transform duration-300"
                              >
                                {copiedSipUri ? (
                                  <CheckCircle className="w-4 h-4 text-success-500" />
                                ) : (
                                  <Copy className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                            <p className="text-xs text-text-enterprise-tertiary mt-1">
                              Your complete SIP URI for browser-based calling
                            </p>
                          </div>

                          {/* VOIP Domain */}
                          <div>
                            <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                              VOIP Domain
                            </label>
                            <div className="px-3 py-2 bg-glass-surface/60 rounded-lg border border-glass-border/30 text-sm text-text-enterprise-primary font-mono">
                              {voipSettings.voip_domain}
                            </div>
                            <p className="text-xs text-text-enterprise-tertiary mt-1">
                              Exotel VOIP server domain
                            </p>
                          </div>

                          {/* Info Box */}
                          <div className="flex items-start gap-3 p-3 bg-info-500/10 rounded-lg border border-info-500/30">
                            <Wifi className="w-5 h-5 text-info-500 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-text-enterprise-secondary space-y-1">
                              <p className="font-semibold text-info-600">How VOIP Calling Works:</p>
                              <ul className="list-disc list-inside space-y-0.5 text-text-enterprise-tertiary">
                                <li>Click "Web Call" button in call script page</li>
                                <li>Call connects through your browser automatically</li>
                                <li>Candidate receives a regular phone call</li>
                                <li>No phone needed on your end - just headset and mic</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Disabled State Info */}
                      {!voipSettings.user_voip_enabled && (
                        <div className="flex items-start gap-3 p-4 bg-gray-500/10 rounded-lg border border-gray-500/30">
                          <WifiOff className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-text-enterprise-secondary">
                            <p className="font-semibold text-gray-600 mb-2">VOIP is Currently Disabled</p>
                            <p className="text-text-enterprise-tertiary">
                              Enable VOIP to make calls directly from your browser without needing a phone. 
                              Perfect for remote work and better call quality.
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default UserProfilePage;